import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import LiveExam from '../../../Services/liveexam.services';
import { ExamService } from '../../../Services/examServices';
import { errorResponseHelper } from '../../../helpers/request.helper';
import AllPagesPDFViewer from './pdfview/AllPagesPdfViewer';
import Auth from '../../../auth/auth';
import { defaultTheme, withAlpha, PageShell, TopBar, UserChip, NxButton, NxIcon } from '../../../components/nx';
import "./Instructions.css";
import { AuthService } from '../../../Services/authServices';
import fingerPrint from '../../../helpers/fingerprintjs';

export interface CustomizedState {
    examId: string;
    mappingId: string;
}

const t = defaultTheme;

export default function Instructions() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as CustomizedState;
    const [profile, setProfile] = useState<any>();

    const [instructionPdf, setInstructionPdf] = useState<string | null>(null);
    const [agreed, setAgreed] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const generateTokenForLiveExam = async () => {
        try {
            const res = await LiveExam.generateTokenForLiveExam(state.examId, state.mappingId);
            if (res.status === 200) {
                sessionStorage.setItem("liveToken", res.data.data.token);
                toast.success("Exam countdown has begun");
                navigate('/live/exam-live', { state: { examId: state.examId, mappingId: state.mappingId } });
            }
        } catch (e) {
            errorResponseHelper(e);
        }
    };

    async function getProfile() {
        try {
            const res = await ExamService.getUserInfoWtihoutExams();
            if (res.status === 200) {
                setProfile(res.data.data.studentInfo[0]);
            }
        } catch (e) { }
    }


    const getExamInstructions = async () => {
        try {
            const res = await ExamService.getInstructions(state.examId);
            if (res.status === 200) {
                const pdf = res.data.data.instructions;
                if (pdf && typeof pdf === 'string' && pdf.length > 0) {
                    setInstructionPdf(pdf);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

     async function handleLogout() {
        try {
            const res = await AuthService.logout({ devInfo: await fingerPrint() });
            if (res.status === 200) {
                Auth.signout();
                localStorage.setItem("authKey", res.data.data.token);
                toast.success("Logged out");
            }
            navigate('/login');
        } catch (err) {
            navigate('/login');
        }
    }

    const profileName = profile ? `${profile.name || ''} ${profile.last_name || ''}`.trim() : 'User';


    useEffect(() => {
        getExamInstructions();
        getProfile();
    }, []);

    return (
        <PageShell t={t} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <TopBar
                t={t}
                crumbs={[{ label: 'Dashboard', path: '/exam/dashboard' }, 'Instructions']}
                onNavigate={(path) => navigate(path)}
                right={<UserChip t={t} name={profileName} email={profile?.email} regNo={profile?.registrationNumber} onLogout={handleLogout} onProfile={() => navigate('/exam/user/profile')} />}
            />

            {/* Body */}
            <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div className="nx-hero" style={{ padding: '36px 56px 24px', borderBottom: `1px solid ${t.border}`, background: t.surface }}>
                    <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600, marginBottom: 8 }}>Online Assessment</div>
                    <h1 style={{
                        fontSize: 32, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.05,
                        color: t.ink, margin: 0,
                    }}>Exam Instructions</h1>
                </div>

                <div className="nx-pad-body" style={{ flex: 1, padding: '32px 56px 120px', display: 'flex', justifyContent: 'center' }}>
                    {instructionPdf ? (
                        <div style={{ width: '100%', maxWidth: 1100 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 14 }}>
                                <NxButton t={t} kind="secondary" onClick={() => setShowPdfModal(true)}>
                                    View Full Screen
                                </NxButton>
                            </div>
                            <div className="all-page-container" style={{ overflow: 'auto', border: `1px solid ${t.border}`, borderRadius: 8 }}>
                                <AllPagesPDFViewer pdf={instructionPdf} width={Math.max(240, Math.min(1060, viewportWidth - 60))} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: t.inkMute, fontSize: 14 }}>
                            Loading instructions...
                        </div>
                    )}
                </div>
            </main>

            {/* Sticky footer */}
            <div className="nx-action-bar" style={{
                borderTop: `1px solid ${t.border}`, background: t.surface,
                padding: '14px 56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
            }}>
                <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: t.inkSoft, cursor: 'pointer' }}
                    onClick={() => setAgreed(!agreed)}
                >
                    <span style={{
                        width: 16, height: 16, borderRadius: 4,
                        background: agreed ? t.accent : 'transparent',
                        border: agreed ? 'none' : `1px solid ${t.borderStrong}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {agreed && NxIcon.check('#fff')}
                    </span>
                    I have read and understood the instructions above.
                </label>
                <div className="nx-action-bar-btns" style={{ display: 'flex', gap: 12 }}>
                    <NxButton t={t} kind="secondary" icon={NxIcon.arrowLeft(t.ink)} onClick={() => navigate('/exam/dashboard')}>
                        Back to dashboard
                    </NxButton>
                    <NxButton t={t} kind="primary" icon={NxIcon.arrowRight('#fff')} disabled={!agreed} onClick={generateTokenForLiveExam}>
                        Begin assessment
                    </NxButton>
                </div>
            </div>

            {/* PDF Fullscreen Modal */}
            {showPdfModal && instructionPdf && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(0,0,0,0.75)',
                        display: 'flex', flexDirection: 'column',
                    }}
                    onClick={() => setShowPdfModal(false)}
                >
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 24px', background: t.ink,
                    }}>
                        <span style={{ color: t.paper, fontSize: 14, fontWeight: 500 }}>Exam Instructions</span>
                        <button
                            onClick={() => setShowPdfModal(false)}
                            style={{
                                background: withAlpha(t.paper, 0.15), color: t.paper, border: 'none',
                                padding: '6px 16px', borderRadius: 6, fontSize: 12,
                                fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                            }}
                        >
                            Close
                        </button>
                    </div>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            flex: 1, overflow: 'auto',
                            display: 'flex', justifyContent: 'center',
                            padding: '24px 0',
                        }}
                    >
                        <AllPagesPDFViewer pdf={instructionPdf} width={Math.max(240, Math.min(900, viewportWidth - 40))} />
                    </div>
                </div>
            )}
        </PageShell>
    );
}
